import type { CreateUserProfileDto } from '@metapic-apps/shared'
import { ref } from 'vue'

import { normalizeCreatorSocialProfile } from '@/lib/adapters/creator-social-profile.adapter'
import { handleErrorMessage } from '@/lib/helpers/error.helpers'
import { socialMediaClient } from '@/lib/services/social-media.service'
import type { CreatorSocialProfile } from '@/lib/types/creator-detail.types'
import { useErrorStore } from '@/stores/error'

const {
  createHandle,
  updateUserProfile,
  _delete: removeUserProfile,
} = socialMediaClient.userProfiles

type AddCreatorSocialProfilePayload = {
  userId: string
  platform: CreateUserProfileDto['platform']
  handle: string
}

export const useAddCreatorSocialProfile = () => {
  const isLoading = ref(false)
  const error = ref<Error | null>(null)
  const errorStore = useErrorStore()

  const addCreatorSocialProfile = async ({
    userId,
    platform,
    handle,
  }: AddCreatorSocialProfilePayload): Promise<CreatorSocialProfile | null> => {
    try {
      isLoading.value = true
      error.value = null
      const response = await createHandle(userId, { platform, handle, is_primary: false })
      return normalizeCreatorSocialProfile(response)
    } catch (e: unknown) {
      errorStore.setError(handleErrorMessage(e))
      error.value = e as Error
      return null
    } finally {
      isLoading.value = false
    }
  }

  return { addCreatorSocialProfile, isLoading, error }
}

export const useSetMainCreatorSocialProfile = () => {
  const isLoading = ref(false)
  const error = ref<Error | null>(null)
  const errorStore = useErrorStore()

  const setMainCreatorSocialProfile = async (
    userId: string,
    profileId: string,
  ): Promise<CreatorSocialProfile | null> => {
    try {
      isLoading.value = true
      error.value = null
      const response = await updateUserProfile(userId, profileId, { is_primary: true })
      return normalizeCreatorSocialProfile(response)
    } catch (e: unknown) {
      errorStore.setError(handleErrorMessage(e))
      error.value = e as Error
      return null
    } finally {
      isLoading.value = false
    }
  }

  return { setMainCreatorSocialProfile, isLoading, error }
}

export const useDeleteCreatorSocialProfile = () => {
  const isLoading = ref(false)
  const error = ref<Error | null>(null)
  const errorStore = useErrorStore()

  const deleteCreatorSocialProfile = async (
    userId: string,
    profileId: string,
  ): Promise<boolean> => {
    try {
      isLoading.value = true
      error.value = null
      await removeUserProfile(userId, profileId)
      return true
    } catch (e: unknown) {
      errorStore.setError(handleErrorMessage(e))
      error.value = e as Error
      return false
    } finally {
      isLoading.value = false
    }
  }

  return { deleteCreatorSocialProfile, isLoading, error }
}
